/**
 * Identità visiva di ogni prodotto nelle pagine di accesso (login, nuova password, ritorno dalla banca).
 * Le classi Tailwind sono scritte per intero (niente stringhe composte) perché vengano generate.
 */
import type { LucideIcon } from "lucide-react";
import { HardHat, LayoutDashboard, Receipt, Truck, Users } from "lucide-react";
import MindleadConstructionIcon from "@/assets/mindlead-construction-icon.svg";
import MindleadCoreIcon from "@/assets/mindlead-core-icon.svg";
import type { ProductKey } from "@/platform/products";

export interface AuthAccent {
  /** testo piccolo sopra il titolo */
  eyebrow: string;
  /** pulsante principale */
  button: string;
  /** bordo e anello dei campi in focus */
  input: string;
  /** link e icone */
  link: string;
  /** riquadro dell'icona quando il prodotto non ha un logo proprio */
  iconTile: string;
  /** sfondo della pagina */
  background: string;
  /** alone di sfondo */
  glow: string;
  /** bordo e ombra della scheda */
  card: string;
}

export interface AuthBrand {
  /** "Mindlead" */
  eyebrow: string;
  /** nome del prodotto */
  name: string;
  /** frase sotto il nome, nella pagina di accesso */
  tagline: string;
  logo?: string;
  icon: LucideIcon;
  accent: AuthAccent;
}

const ACCENTS = {
  emerald: {
    eyebrow: "text-emerald-700/70",
    button: "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-900/10",
    input: "focus-visible:border-emerald-400 focus-visible:ring-emerald-400/30",
    link: "text-emerald-700 hover:text-emerald-800",
    iconTile: "bg-emerald-600 text-white",
    background: "bg-gradient-to-br from-emerald-50 via-white to-slate-50",
    glow: "bg-[radial-gradient(ellipse_at_top,_rgba(34,168,97,0.14),_transparent_60%)]",
    card: "border-emerald-100/80 shadow-emerald-900/5",
  },
  sky: {
    eyebrow: "text-sky-700/70",
    button: "bg-sky-600 hover:bg-sky-700 shadow-sky-900/10",
    input: "focus-visible:border-sky-400 focus-visible:ring-sky-400/30",
    link: "text-sky-700 hover:text-sky-800",
    iconTile: "bg-sky-600 text-white",
    background: "bg-gradient-to-br from-sky-50 via-white to-slate-50",
    glow: "bg-[radial-gradient(ellipse_at_top,_rgba(37,99,235,0.14),_transparent_60%)]",
    card: "border-sky-100/80 shadow-sky-900/5",
  },
  indigo: {
    eyebrow: "text-indigo-700/70",
    button: "bg-indigo-600 hover:bg-indigo-700 shadow-indigo-900/10",
    input: "focus-visible:border-indigo-400 focus-visible:ring-indigo-400/30",
    link: "text-indigo-700 hover:text-indigo-800",
    iconTile: "bg-indigo-600 text-white",
    background: "bg-gradient-to-br from-indigo-50 via-white to-slate-50",
    glow: "bg-[radial-gradient(ellipse_at_top,_rgba(79,70,229,0.14),_transparent_60%)]",
    card: "border-indigo-100/80 shadow-indigo-900/5",
  },
  violet: {
    eyebrow: "text-violet-700/70",
    button: "bg-violet-600 hover:bg-violet-700 shadow-violet-900/10",
    input: "focus-visible:border-violet-400 focus-visible:ring-violet-400/30",
    link: "text-violet-700 hover:text-violet-800",
    iconTile: "bg-violet-600 text-white",
    background: "bg-gradient-to-br from-violet-50 via-white to-slate-50",
    glow: "bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.14),_transparent_60%)]",
    card: "border-violet-100/80 shadow-violet-900/5",
  },
  amber: {
    eyebrow: "text-amber-700/70",
    button: "bg-amber-600 hover:bg-amber-700 shadow-amber-900/10",
    input: "focus-visible:border-amber-400 focus-visible:ring-amber-400/30",
    link: "text-amber-700 hover:text-amber-800",
    iconTile: "bg-amber-600 text-white",
    background: "bg-gradient-to-br from-amber-50 via-white to-slate-50",
    glow: "bg-[radial-gradient(ellipse_at_top,_rgba(217,119,6,0.14),_transparent_60%)]",
    card: "border-amber-100/80 shadow-amber-900/5",
  },
} satisfies Record<string, AuthAccent>;

export const AUTH_BRANDS: Record<ProductKey, AuthBrand> = {
  construction: {
    eyebrow: "Mindlead",
    name: "Construction",
    tagline: "Accedi al gestionale di cantieri e commesse",
    logo: MindleadConstructionIcon,
    icon: HardHat,
    accent: ACCENTS.emerald,
  },
  console: {
    eyebrow: "Mindlead",
    name: "Suite",
    tagline: "Console di amministrazione della piattaforma",
    logo: MindleadCoreIcon,
    icon: LayoutDashboard,
    accent: ACCENTS.sky,
  },
  billing: {
    eyebrow: "Mindlead",
    name: "Fatturazione",
    tagline: "Accedi a fatture, scadenze e banca",
    icon: Receipt,
    accent: ACCENTS.indigo,
  },
  crm: {
    eyebrow: "Mindlead",
    name: "CRM",
    tagline: "Accedi a clienti e opportunità",
    icon: Users,
    accent: ACCENTS.violet,
  },
  supply: {
    eyebrow: "Mindlead",
    name: "Supply",
    tagline: "Accedi al portale fornitori",
    icon: Truck,
    accent: ACCENTS.amber,
  },
};

/**
 * Prodotto di cui mostrare il brand in una pagina condivisa (/auth/…): quello del dominio, oppure
 * quello da cui l'utente è partito, altrimenti la Suite.
 */
export function sharedPageProduct(current: ProductKey | null, fromPath?: string | null, ownerOf?: (p: string) => ProductKey | null): ProductKey {
  if (current) return current;
  const fromOwner = fromPath && ownerOf ? ownerOf(fromPath) : null;
  return fromOwner ?? "console";
}
